import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { 
  Users, 
  Smartphone, 
  Eye, 
  TrendingUp, 
  Activity,
  Store,
  ShoppingBag,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const RealtimeAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/admin/analytics/realtime');
      setAnalytics(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Erro ao buscar analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center text-gray-500 py-8">
        Erro ao carregar métricas
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue' }) => (
    <div className="bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subValue && <p className="text-xs text-gray-400">{subValue}</p>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">📊 Analytics em Tempo Real</h2>
          {lastUpdate && (
            <p className="text-sm text-gray-500">
              Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')}
            </p>
          )}
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchAnalytics}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {/* Usuários Online - Destaque */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-100 text-sm">Usuários Online Agora</p>
            <p className="text-5xl font-bold mt-1">{analytics.realtime.total_online}</p>
            <div className="flex gap-4 mt-2 text-sm text-green-100">
              <span>👤 {analytics.realtime.online_users} logados</span>
              <span>👁️ {analytics.realtime.anonymous_sessions} visitantes</span>
            </div>
          </div>
          <div className="relative">
            <Activity className="w-16 h-16 text-green-200 animate-pulse" />
            <span className="absolute top-0 right-0 w-3 h-3 bg-green-300 rounded-full animate-ping"></span>
          </div>
        </div>
      </div>

      {/* Cards Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total de Usuários"
          value={analytics.users.total}
          subValue={`${analytics.users.by_type.cliente || 0} clientes`}
          color="blue"
        />
        <StatCard
          icon={Smartphone}
          label="Instalações PWA"
          value={analytics.pwa.total_installs}
          subValue={`+${analytics.pwa.installs_today} hoje`}
          color="purple"
        />
        <StatCard
          icon={Eye}
          label="Visitas Hoje"
          value={analytics.visits.today}
          subValue={`${analytics.visits.week} na semana`}
          color="orange"
        />
        <StatCard
          icon={ShoppingBag}
          label="Pedidos Hoje"
          value={analytics.orders.today}
          color="red"
        />
      </div>

      {/* Detalhes de Usuários */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usuários por Tipo */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Usuários por Tipo
          </h3>
          <div className="space-y-3">
            {Object.entries(analytics.users.by_type).map(([type, count]) => {
              const percentage = analytics.users.total > 0 
                ? Math.round((count / analytics.users.total) * 100) 
                : 0;
              const colors = {
                cliente: 'bg-blue-500',
                lojista: 'bg-green-500',
                entregador: 'bg-orange-500',
                admin: 'bg-purple-500'
              };
              const labels = {
                cliente: 'Clientes',
                lojista: 'Lojistas',
                entregador: 'Entregadores',
                admin: 'Administradores'
              };
              
              return (
                <div key={type}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{labels[type] || type}</span>
                    <span className="font-medium">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`${colors[type] || 'bg-gray-500'} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Visitas por Dia */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-orange-600" />
            Visitas - Últimos 7 Dias
          </h3>
          <div className="space-y-2">
            {analytics.visits.by_day.map((day, index) => {
              const maxVisits = Math.max(...analytics.visits.by_day.map(d => d.count), 1);
              const percentage = Math.round((day.count / maxVisits) * 100);
              const date = new Date(day.date + 'T12:00:00');
              const dayName = date.toLocaleDateString('pt-BR', { weekday: 'short' });
              const dayNum = date.getDate();
              
              return (
                <div key={day.date} className="flex items-center gap-3">
                  <span className="w-16 text-sm text-gray-500 capitalize">
                    {dayName} {dayNum}
                  </span>
                  <div className="flex-1 bg-gray-200 rounded-full h-4">
                    <div 
                      className="bg-gradient-to-r from-orange-400 to-orange-600 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(percentage, 5)}%` }}
                    >
                      {day.count > 0 && (
                        <span className="text-xs text-white font-medium">{day.count}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{analytics.visits.today}</p>
              <p className="text-xs text-gray-500">Hoje</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{analytics.visits.week}</p>
              <p className="text-xs text-gray-500">Semana</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{analytics.visits.month}</p>
              <p className="text-xs text-gray-500">Mês</p>
            </div>
          </div>
        </div>
      </div>

      {/* Páginas Mais Visitadas */}
      {analytics.top_pages && analytics.top_pages.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            Páginas Mais Visitadas Hoje
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b">
                  <th className="pb-2">#</th>
                  <th className="pb-2">Página</th>
                  <th className="pb-2 text-right">Visitas</th>
                </tr>
              </thead>
              <tbody>
                {analytics.top_pages.map((page, index) => (
                  <tr key={page.page} className="border-b last:border-0">
                    <td className="py-2 text-gray-400">{index + 1}</td>
                    <td className="py-2 font-medium text-gray-700">
                      {page.page === '/' ? 'Página Inicial' : page.page}
                    </td>
                    <td className="py-2 text-right">
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm">
                        {page.count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resumo Rápido */}
      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-700 mb-2">📈 Resumo</p>
        <ul className="space-y-1">
          <li>• <strong>{analytics.stores.active}</strong> lojas ativas no marketplace</li>
          <li>• <strong>{analytics.pwa.total_installs}</strong> pessoas instalaram o app</li>
          <li>• <strong>{analytics.visits.month}</strong> visitas este mês</li>
        </ul>
      </div>
    </div>
  );
};

export default RealtimeAnalytics;
